import { atom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useRef } from "react";

export type LeaveReason = "section-change" | "modal-close" | "document-change" | "logout";

/** Данные о действии, перед которыми запускается проверка возможности ухода. */
export type LeaveContext = {
  reason: LeaveReason;
  scope?: string;
  value?: unknown;
};

type LeaveEntryBase = {
  id: string;
  scope: string;
  priority?: number;
  beforeUnload?: boolean;
};

type LeaveBlockerEntry = LeaveEntryBase & {
  kind: "blocker";
  canLeave: (context: LeaveContext) => boolean | Promise<boolean>;
};

type LeaveEffectEntry = LeaveEntryBase & {
  kind: "effect";
  once: boolean;
  run: (context: LeaveContext) => void | Promise<void>;
};

type LeaveEntry = LeaveBlockerEntry | LeaveEffectEntry;

type LeaveEntryIdentity = Pick<LeaveEntryBase, "id" | "scope">;

export type LeaveBlockerOptions = LeaveEntryBase & {
  enabled?: boolean;
  canLeave: LeaveBlockerEntry["canLeave"];
};

export type LeaveEffectOptions = LeaveEntryBase & {
  enabled?: boolean;
  once?: boolean;
  run: LeaveEffectEntry["run"];
};

/** Глобальные обработчики запускаются при запросе ухода из любого scope. */
export const globalLeaveScope = "global";

/** Возвращает scope секции или глобальный scope, если идентификатор секции отсутствует. */
export const createSectionLeaveScope = (sectionId?: string | number) => {
  return sectionId === undefined || sectionId === "" ? globalLeaveScope : `section:${sectionId}`;
};

/** Возвращает scope конкретного модального окна. */
export const createModalLeaveScope = (modalId: string) => {
  return modalId ? `modal:${modalId}` : globalLeaveScope;
};

const getLeaveEntryKey = ({ id, scope }: LeaveEntryIdentity) => {
  return `${scope}:${id}`;
};

const leaveEntriesAtom = atom<ReadonlyMap<string, LeaveEntry>>(new Map());
const leaveRequestInProgressAtom = atom(false);

/** Внутренний атом регистрации. Одинаковые id внутри одного scope заменяют друг друга. */
const registerLeaveEntryAtom = atom(null, (_get, set, entry: LeaveEntry) => {
  set(leaveEntriesAtom, (currentEntries) => {
    const nextEntries = new Map(currentEntries);
    nextEntries.set(getLeaveEntryKey(entry), entry);
    return nextEntries;
  });
});

/** Удаляет зарегистрированный обработчик по его id и scope. */
export const unregisterLeaveEntryAtom = atom(null, (_get, set, identity: LeaveEntryIdentity) => {
  set(leaveEntriesAtom, (currentEntries) => {
    const entryKey = getLeaveEntryKey(identity);
    if (!currentEntries.has(entryKey)) return currentEntries;

    const nextEntries = new Map(currentEntries);
    nextEntries.delete(entryKey);
    return nextEntries;
  });
});

const isEntryInRequestScope = (entry: LeaveEntry, requestScope?: string) => {
  return requestScope === undefined || entry.scope === globalLeaveScope || entry.scope === requestScope;
};

const sortByPriority = (firstEntry: LeaveEntry, secondEntry: LeaveEntry) => {
  return (secondEntry.priority ?? 0) - (firstEntry.priority ?? 0);
};

/**
 * Проверяет возможность ухода и запускает связанные эффекты.
 *
 * Сначала последовательно выполняются blockers. Если хотя бы один вернул false
 * или завершился с ошибкой, уход отменяется и effects не запускаются.
 * После успешных blockers последовательно выполняются effects.
 */
export const requestLeaveAtom = atom(null, async (get, set, context: LeaveContext): Promise<boolean> => {
  // Не открываем несколько подтверждений и не запускаем сохранение повторно.
  if (get(leaveRequestInProgressAtom)) return false;

  set(leaveRequestInProgressAtom, true);
  try {
    const entries = Array.from(get(leaveEntriesAtom).values())
      .filter((entry) => isEntryInRequestScope(entry, context.scope))
      .sort(sortByPriority);
    const blockers = entries.filter((entry): entry is LeaveBlockerEntry => entry.kind === "blocker");

    for (const blocker of blockers) {
      try {
        if (!(await blocker.canLeave(context))) return false;
      } catch (error: unknown) {
        console.error(`Leave blocker "${blocker.id}" failed`, error);
        return false;
      }
    }

    const effects = entries.filter((entry): entry is LeaveEffectEntry => entry.kind === "effect");
    for (const effect of effects) {
      try {
        await effect.run(context);
        if (effect.once) {
          set(unregisterLeaveEntryAtom, effect);
        }
      } catch (error: unknown) {
        console.error(`Leave effect "${effect.id}" failed`, error);
        return false;
      }
    }

    return true;
  } finally {
    set(leaveRequestInProgressAtom, false);
  }
});

/** Показывает, есть ли обработчик, требующий системного предупреждения перед перезагрузкой страницы. */
export const hasBeforeUnloadLeaveGuardAtom = atom((get) => {
  return Array.from(get(leaveEntriesAtom).values()).some((entry) => entry.beforeUnload === true);
});

/**
 * Декларативно регистрирует проверку возможности ухода.
 * Обработчик автоматически обновляется и удаляется вместе с React-компонентом.
 */
export const useLeaveBlocker = ({
  id,
  scope,
  enabled = true,
  priority,
  beforeUnload,
  canLeave,
}: LeaveBlockerOptions) => {
  const registerLeaveEntry = useSetAtom(registerLeaveEntryAtom);
  const unregisterLeaveEntry = useSetAtom(unregisterLeaveEntryAtom);
  const canLeaveRef = useRef(canLeave);
  canLeaveRef.current = canLeave;

  useEffect(() => {
    if (!enabled) return;

    registerLeaveEntry({
      id,
      scope,
      kind: "blocker",
      priority,
      beforeUnload,
      canLeave: (context) => canLeaveRef.current(context),
    });

    return () => {
      unregisterLeaveEntry({ id, scope });
    };
  }, [beforeUnload, enabled, id, priority, registerLeaveEntry, scope, unregisterLeaveEntry]);
};

/**
 * Декларативно регистрирует действие, которое выполняется после успешных blockers.
 * По умолчанию эффект удаляется после первого успешного выполнения.
 */
export const useLeaveEffect = ({
  id,
  scope,
  enabled = true,
  priority,
  beforeUnload,
  once = true,
  run,
}: LeaveEffectOptions) => {
  const registerLeaveEntry = useSetAtom(registerLeaveEntryAtom);
  const unregisterLeaveEntry = useSetAtom(unregisterLeaveEntryAtom);
  const runRef = useRef(run);
  runRef.current = run;

  useEffect(() => {
    if (!enabled) return;

    registerLeaveEntry({
      id,
      scope,
      kind: "effect",
      priority,
      beforeUnload,
      once,
      run: (context) => runRef.current(context),
    });

    return () => {
      unregisterLeaveEntry({ id, scope });
    };
  }, [beforeUnload, enabled, id, once, priority, registerLeaveEntry, scope, unregisterLeaveEntry]);
};

/**
 * Позволяет регистрировать blockers и effects из событий сторонних компонентов,
 * например из onGridStateChange. Все созданные записи удаляются при unmount.
 */
export const useLeaveRegistry = () => {
  const registerLeaveEntry = useSetAtom(registerLeaveEntryAtom);
  const unregisterLeaveEntry = useSetAtom(unregisterLeaveEntryAtom);
  const ownedEntriesRef = useRef<Map<string, LeaveEntryIdentity>>(new Map());

  const registerOwnedEntry = useCallback(
    (entry: LeaveEntry) => {
      const entryKey = getLeaveEntryKey(entry);
      ownedEntriesRef.current.set(entryKey, entry);
      registerLeaveEntry(entry);

      return () => {
        ownedEntriesRef.current.delete(entryKey);
        unregisterLeaveEntry(entry);
      };
    },
    [registerLeaveEntry, unregisterLeaveEntry],
  );

  const registerLeaveBlocker = useCallback(
    ({ enabled = true, ...options }: LeaveBlockerOptions) => {
      if (!enabled) return () => undefined;
      return registerOwnedEntry({ ...options, kind: "blocker" });
    },
    [registerOwnedEntry],
  );

  const registerLeaveEffect = useCallback(
    ({ enabled = true, once = true, ...options }: LeaveEffectOptions) => {
      if (!enabled) return () => undefined;
      return registerOwnedEntry({ ...options, kind: "effect", once });
    },
    [registerOwnedEntry],
  );

  const unregisterLeaveHandler = useCallback(
    (identity: LeaveEntryIdentity) => {
      ownedEntriesRef.current.delete(getLeaveEntryKey(identity));
      unregisterLeaveEntry(identity);
    },
    [unregisterLeaveEntry],
  );

  useEffect(() => {
    return () => {
      ownedEntriesRef.current.forEach((identity) => {
        unregisterLeaveEntry(identity);
      });
      ownedEntriesRef.current.clear();
    };
  }, [unregisterLeaveEntry]);

  return {
    registerLeaveBlocker,
    registerLeaveEffect,
    unregisterLeaveHandler,
  };
};

/**
 * Подключает системный beforeunload только при наличии активного обработчика
 * с beforeUnload: true. Асинхронные blockers здесь не выполняются, поскольку
 * браузер не ожидает их завершения.
 */
export const useLeaveBeforeUnload = (onBeforeUnload?: () => void) => {
  const hasBeforeUnloadGuard = useAtomValue(hasBeforeUnloadLeaveGuardAtom);
  const onBeforeUnloadRef = useRef(onBeforeUnload);
  onBeforeUnloadRef.current = onBeforeUnload;

  useEffect(() => {
    if (!hasBeforeUnloadGuard) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      onBeforeUnloadRef.current?.();
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [hasBeforeUnloadGuard]);
};
